import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { adminAPI } from '../config/api';
import {
  formatMoney,
  formatRentalDate,
  getBookedFor,
  getBookedFrom,
  getDropoffTime,
  getRentalDays,
  getRentalLineTotal,
} from '../utils/rentalFields';
import swal from 'sweetalert';
import AdminSidebar from '../components/AdminSidebar';
import { showAppToast } from '../utils/notifications';

const roleChoices = [
  { value: 'user', label: 'Customer' },
  { value: 'admin', label: 'Super Admin' },
  { value: 'fleet_manager', label: 'Fleet Manager' },
  { value: 'reservations_manager', label: 'Reservations Manager' },
  { value: 'support_agent', label: 'Support Agent' },
  { value: 'account_manager', label: 'Account Manager' },
];

const permissionLabels = {
  'platform.full_access': 'Full access',
  'bookings.manage': 'Bookings',
  'fleet.manage': 'Fleet',
  'categories.manage': 'Categories',
  'users.manage': 'Accounts',
  'messages.manage': 'Messages',
  'analytics.view': 'Analytics',
  'settings.manage': 'Settings',
};

function getUserName(user) {
  return `${user.firstName || ''} ${user.lastName || ''}`.trim() || user.email || 'Unnamed user';
}

function getRoleLabel(role) {
  return roleChoices.find((choice) => choice.value === role)?.label || role?.replaceAll('_', ' ') || 'Customer';
}

function getPermissionLabels(user) {
  return (Array.isArray(user.permissions) ? user.permissions : [])
    .map((scope) => permissionLabels[scope] || scope)
    .slice(0, 4);
}

function Users() {
  const [users, setUsers] = useState([]);
  const [cartItems, setCartItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('All roles');
  const [sortMode, setSortMode] = useState('Name');

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  const fetchAdminUsers = async () => {
    setLoading(true);
    try {
      const [usersResponse, cartsResponse] = await Promise.all([
        adminAPI.getUsers(),
        adminAPI.getAllCarts(),
      ]);
      setUsers(usersResponse.data || []);
      setCartItems(cartsResponse.data?.cartItems || []);
    } catch (error) {
      console.error('Failed to fetch admin users:', error);
      showAppToast({ message: 'Failed to load users. Please try again.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const bookingsByUser = useMemo(() => {
    const map = new Map();
    cartItems.forEach((item) => {
      const userId = item.userId || item.user?.id;
      if (!map.has(userId)) map.set(userId, []);
      map.get(userId).push(item);
    });
    return map;
  }, [cartItems]);

  const roleOptions = useMemo(() => {
    const roles = new Set((users || []).map((user) => (user.role || 'user').toLowerCase()));
    return ['All roles', ...Array.from(roles).sort()];
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = searchTerm.toLowerCase();
    return users
      .filter((user) => {
        const matchesSearch = (
          user.firstName?.toLowerCase().includes(term) ||
          user.lastName?.toLowerCase().includes(term) ||
          user.email?.toLowerCase().includes(term) ||
          user.role?.toLowerCase().includes(term)
        );
        const matchesRole = roleFilter === 'All roles' || (user.role || 'user').toLowerCase() === roleFilter;
        return matchesSearch && matchesRole;
      })
      .sort((first, second) => {
        if (sortMode === 'Most bookings') {
          return (bookingsByUser.get(second.id)?.length || 0) - (bookingsByUser.get(first.id)?.length || 0);
        }
        if (sortMode === 'Highest value') {
          const firstTotal = (bookingsByUser.get(first.id) || []).reduce((sum, booking) => sum + getRentalLineTotal(booking), 0);
          const secondTotal = (bookingsByUser.get(second.id) || []).reduce((sum, booking) => sum + getRentalLineTotal(booking), 0);
          return secondTotal - firstTotal;
        }
        return getUserName(first).localeCompare(getUserName(second));
      });
  }, [bookingsByUser, roleFilter, searchTerm, sortMode, users]);

  const accountStats = useMemo(() => {
    const customers = users.filter((user) => (user.role || 'user').toLowerCase() === 'user').length;
    const teamMembers = users.length - customers;
    const bookedAccounts = users.filter((user) => (bookingsByUser.get(user.id) || []).length > 0).length;
    return [
      { label: 'Total accounts', value: users.length, icon: 'bi-people' },
      { label: 'Customers', value: customers, icon: 'bi-person-check' },
      { label: 'Team members', value: teamMembers, icon: 'bi-person-gear' },
      { label: 'With active bookings', value: bookedAccounts, icon: 'bi-calendar2-check' },
    ];
  }, [bookingsByUser, users]);

  const deleteUser = async (user) => {
    const result = await swal({
      title: 'Delete User?',
      text: `Delete ${getUserName(user)} and all account-linked records?`,
      icon: 'warning',
      buttons: ['Cancel', 'Delete'],
      dangerMode: true,
    });

    if (!result) return;

    setSaving(true);
    try {
      await adminAPI.deleteUser(user.id);
      showAppToast({ message: 'User deleted successfully', type: 'success', duration: 2400 });
      await fetchAdminUsers();
    } catch (error) {
      showAppToast({ message: error.response?.data?.message || 'Failed to delete user', type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="admin-layout">
        <AdminSidebar />
        <div className="admin-content px-4 py-16">
          <div className="admin-route-loading-card card-luxury" role="status" aria-live="polite">
            <i className="bi bi-people-fill" aria-hidden="true" />
            <strong>Loading users</strong>
            <p className="text-secondary-theme">Preparing account access records...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <AdminSidebar />
      <div className="admin-content px-4 py-8">
        <div className="mx-auto max-w-7xl">
          <div className="admin-header">
            <div>
              <p className="text-accent">Customer management</p>
              <h1 className="section-title text-4xl text-primary-theme">Customers &amp; accounts</h1>
              <p className="text-secondary-theme">
                Find customer details, review booking activity, and manage team access.
              </p>
            </div>
            <Link to="/admin/users/new" className="btn-luxury admin-add-booking-button">
              <i className="bi bi-plus-lg" />
              Add account
            </Link>
          </div>

          <section className="admin-access-stats admin-user-stats" aria-label="Account summary">
            {accountStats.map((stat) => (
              <article key={stat.label} className="admin-stat-card">
                <div><p>{stat.label}</p><strong>{stat.value}</strong></div>
                <i className={`bi ${stat.icon}`} aria-hidden="true" />
              </article>
            ))}
          </section>

          <div className="admin-filter-strip">
            <label className="admin-search-field">
              <i className="bi bi-search" aria-hidden="true" />
              <span className="sr-only">Search accounts</span>
              <input
                type="search"
                placeholder="Search name, email, or role"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                className="admin-search"
              />
            </label>
            <label className="admin-filter-field">
              <span>Role</span>
              <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} aria-label="Filter users by role">
                {roleOptions.map((role) => (
                  <option key={role}>{role}</option>
                ))}
              </select>
            </label>
            <label className="admin-filter-field">
              <span>Sort by</span>
              <select value={sortMode} onChange={(event) => setSortMode(event.target.value)} aria-label="Sort users">
                <option>Name</option>
                <option>Most bookings</option>
                <option>Highest value</option>
              </select>
            </label>
            <span className="admin-filter-result">{filteredUsers.length} result{filteredUsers.length === 1 ? '' : 's'}</span>
          </div>

          <div className="admin-table-card card-luxury">
            <div className="overflow-x-auto">
              <table className="admin-table admin-user-table">
                <thead>
                  <tr>
                    <th>Account</th>
                    <th>Role</th>
                    <th>Access</th>
                    <th>Bookings</th>
                    <th>Active value</th>
                    <th className="text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const bookings = bookingsByUser.get(user.id) || [];
                    const userTotal = bookings.reduce((sum, booking) => sum + getRentalLineTotal(booking), 0);
                    return (
                      <tr key={user.id}>
                        <td>
                          <div className="admin-user-identity">
                            <span className="admin-user-avatar" aria-hidden="true">{getUserName(user).charAt(0).toUpperCase()}</span>
                            <div>
                              <strong>{getUserName(user)}</strong>
                              <a href={`mailto:${user.email}`}>{user.email}</a>
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="admin-role-pill">{getRoleLabel(user.role || 'user')}</span>
                        </td>
                        <td>
                          <div className="admin-permission-chip-row">
                            {getPermissionLabels(user).length ? (
                              getPermissionLabels(user).map((label) => (
                                <span key={label} className="admin-permission-chip">{label}</span>
                              ))
                            ) : (
                              <span className="text-secondary-theme">Portal access</span>
                            )}
                          </div>
                        </td>
                        <td>
                          {bookings.length ? (
                            <div className="admin-user-bookings-list">
                              {bookings.slice(0, 2).map((booking) => (
                                <div key={booking.id} className="admin-user-booking">
                                  <strong>{booking.product?.name}</strong>
                                  <p>
                                    {formatRentalDate(getBookedFrom(booking))} to {formatRentalDate(getBookedFor(booking))}
                                    {getDropoffTime(booking) ? ` at ${getDropoffTime(booking)}` : ''}
                                  </p>
                                  <p>{getRentalDays(booking)} days | {booking.bookingStatus || 'Pending Review'}</p>
                                </div>
                              ))}
                              {bookings.length > 2 && <small className="admin-user-more">+{bookings.length - 2} more bookings</small>}
                            </div>
                          ) : (
                            <span className="text-secondary-theme">No active bookings</span>
                          )}
                        </td>
                        <td>{formatMoney(userTotal)}</td>
                        <td className="text-right">
                          <div className="admin-row-actions" aria-label={`Actions for ${getUserName(user)}`}>
                            <a
                              href={`mailto:${user.email}`}
                              className="admin-icon-action"
                              aria-label={`Email ${getUserName(user)}`}
                              title={`Email ${getUserName(user)}`}
                            >
                              <i className="bi bi-envelope" aria-hidden="true" />
                            </a>
                            <Link
                              to={`/admin/users/${user.id}/edit`}
                              className="admin-icon-action"
                              aria-label={`Edit ${getUserName(user)}`}
                              title={`Edit ${getUserName(user)}`}
                            >
                              <i className="bi bi-pencil-square" aria-hidden="true" />
                            </Link>
                            <button
                              type="button"
                              className="admin-icon-action admin-icon-action--danger"
                              onClick={() => deleteUser(user)}
                              disabled={saving}
                              aria-label={`Delete ${getUserName(user)}`}
                              title={`Delete ${getUserName(user)}`}
                            >
                              <i className="bi bi-trash3-fill" aria-hidden="true" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {!filteredUsers.length && (
                    <tr>
                      <td colSpan="6" className="py-10 text-center text-secondary-theme">
                        {searchTerm || roleFilter !== 'All roles'
                          ? 'No users found matching your filters'
                          : 'No users yet'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Users;
